import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Save, RefreshCw, Trash2, ArrowLeft, LogOut, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getRoutes, updateRoutePrice, loginAdmin, deleteRoute, createRoute } from '../api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const AdminPage = () => {
  const [routes, setRoutes] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  // New Route State
  const [newRoute, setNewRoute] = useState({ from: '', to: '', price: '' });
  const [isAddOpen, setIsAddOpen] = useState(false);

  // Load routes on auth
  const fetchRoutes = async () => {
    setLoading(true);
    const data = await getRoutes();
    if (data && data.length > 0) {
        setRoutes(data);
    } else {
        setRoutes([]);
    }
    setLoading(false);
  };

  const handleLogin = async () => {
    try {
        await loginAdmin(password);
        setIsAuthenticated(true);
        toast.success('Giriş başarılı');
        fetchRoutes();
    } catch (e) {
        toast.error('Hatalı şifre');
    }
  };

  const handlePriceChange = (index, newPrice) => {
    const newRoutes = [...routes];
    newRoutes[index].price = parseInt(newPrice) || 0;
    setRoutes(newRoutes);
  };

  const handleSave = async (route) => {
    try {
        await updateRoutePrice(route._id, route.price);
        toast.success(`${route.from} -> ${route.to} fiyatı güncellendi!`);
    } catch (e) {
        toast.error('Güncelleme hatası');
    }
  };

  const handleDelete = async (id) => {
    if(window.confirm('Bu rotayı silmek istediğinize emin misiniz?')) {
        try {
            await deleteRoute(id);
            toast.success('Rota silindi');
            fetchRoutes();
        } catch(e) {
            toast.error('Silme işlemi başarısız');
        }
    }
  };

  const handleAddRoute = async () => {
    if (!newRoute.from || !newRoute.to || !newRoute.price) {
        toast.error('Lütfen tüm alanları doldurun');
        return;
    }

    try {
        await createRoute({
            from: newRoute.from,
            to: newRoute.to,
            price: parseInt(newRoute.price)
        });
        toast.success('Yeni rota eklendi');
        setIsAddOpen(false);
        setNewRoute({ from: '', to: '', price: '' });
        fetchRoutes();
    } catch (e) {
        toast.error('Ekleme başarısız');
    }
  };

  if (!isAuthenticated) {
    return (
        <div className="min-h-screen bg-black flex items-center justify-center text-white p-4">
            <div className="w-full max-w-md p-8 bg-[#1a1a1a] rounded-2xl border border-white/10 space-y-6 shadow-2xl">
                <div className="text-center">
                    <h1 className="text-2xl font-bold mb-2">Yönetici Girişi</h1>
                    <p className="text-gray-400 text-sm">Fiyatları düzenlemek için giriş yapın</p>
                </div>
                <div className="space-y-4">
                    <Input 
                        type="password" 
                        placeholder="Şifre" 
                        className="bg-black/50 border-white/20 text-white h-12"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <Button onClick={handleLogin} className="w-full bg-yellow-500 text-black hover:bg-yellow-400 h-12 font-bold">
                        Giriş Yap
                    </Button>
                </div>
                <div className="text-center pt-4 border-t border-white/5">
                    <Link to="/" className="text-sm text-gray-500 hover:text-white flex items-center justify-center gap-2">
                        <ArrowLeft className="h-4 w-4" /> Ana Sayfaya Dön
                    </Link>
                </div>
            </div>
        </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f10] text-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4 w-full md:w-auto">
                <Link to="/">
                    <Button variant="outline" size="icon" className="text-black bg-white hover:bg-gray-200 border-0 h-10 w-10">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                </Link>
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold">Fiyat Yönetim Paneli</h1>
                    <p className="text-sm text-gray-400">Toplam {routes.length} rota listeleniyor</p>
                </div>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
                {/* Add Route Dialog */}
                <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-yellow-500 text-black hover:bg-yellow-400 flex-1 md:flex-none">
                            <Plus className="mr-2 h-4 w-4" /> Yeni Rota Ekle
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#1a1a1a] border-white/10 text-white">
                        <DialogHeader>
                            <DialogTitle>Yeni Rota Ekle</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                            <div className="space-y-2">
                                <Label>Nereden</Label>
                                <Input 
                                    value={newRoute.from} 
                                    onChange={(e) => setNewRoute({...newRoute, from: e.target.value})}
                                    placeholder="Örn: Antalya Havalimanı"
                                    className="bg-black/50 border-white/20"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Nereye</Label>
                                <Input 
                                    value={newRoute.to} 
                                    onChange={(e) => setNewRoute({...newRoute, to: e.target.value})}
                                    placeholder="Örn: Kaş"
                                    className="bg-black/50 border-white/20"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Fiyat (TL)</Label>
                                <Input 
                                    type="number"
                                    value={newRoute.price} 
                                    onChange={(e) => setNewRoute({...newRoute, price: e.target.value})}
                                    placeholder="Örn: 5000"
                                    className="bg-black/50 border-white/20"
                                />
                            </div>
                            <Button onClick={handleAddRoute} className="w-full bg-green-600 hover:bg-green-700 mt-4">
                                Ekle
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>

                <Button onClick={fetchRoutes} variant="outline" className="flex-1 md:flex-none text-black bg-white hover:bg-gray-200 border-0">
                    <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Yenile
                </Button>
                <Button onClick={() => setIsAuthenticated(false)} variant="destructive" className="flex-1 md:flex-none">
                    <LogOut className="mr-2 h-4 w-4" /> Çıkış
                </Button>
            </div>
        </div>

        <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden shadow-xl">
            {/* Header */}
            <div className="hidden md:grid grid-cols-12 gap-4 p-4 border-b border-white/10 font-bold text-gray-400 bg-black/40 text-sm uppercase tracking-wider">
                <div className="col-span-4">Nereden</div>
                <div className="col-span-4">Nereye</div>
                <div className="col-span-2">Fiyat (TL)</div>
                <div className="col-span-2 text-right">İşlem</div>
            </div>
            
            {/* List */}
            <div className="divide-y divide-white/5 max-h-[70vh] overflow-y-auto">
                {routes.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        Henüz hiç rota eklenmemiş. "Yeni Rota Ekle" butonunu kullanın.
                    </div>
                ) : (
                    routes.map((route, idx) => (
                        <div key={route._id || idx} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 items-center hover:bg-white/5 transition-colors">
                            <div className="col-span-4 text-white font-medium flex items-center justify-between md:justify-start">
                                <span className="md:hidden text-gray-500 text-xs uppercase">Nereden:</span>
                                {route.from}
                            </div>
                            <div className="col-span-4 text-white font-medium flex items-center justify-between md:justify-start">
                                <span className="md:hidden text-gray-500 text-xs uppercase">Nereye:</span>
                                {route.to}
                            </div>
                            <div className="col-span-2">
                                 <div className="flex items-center gap-2">
                                    <span className="md:hidden text-gray-500 text-xs uppercase w-20">Fiyat:</span>
                                    <Input 
                                        type="number" 
                                        value={route.price} 
                                        onChange={(e) => handlePriceChange(idx, e.target.value)}
                                        className="bg-black/40 border-white/10 text-white h-9 w-full font-bold text-yellow-500"
                                    />
                                 </div>
                            </div>
                            <div className="col-span-2 flex gap-2 justify-end mt-2 md:mt-0">
                                <Button onClick={() => handleSave(route)} size="sm" className="bg-green-600 hover:bg-green-700 text-white flex-1 md:flex-none">
                                    <Save className="h-4 w-4 md:mr-0 mr-2" /> <span className="md:hidden">Kaydet</span>
                                </Button>
                                <Button onClick={() => handleDelete(route._id)} size="sm" variant="ghost" className="text-red-500 hover:text-red-400 hover:bg-red-900/20 flex-1 md:flex-none">
                                    <Trash2 className="h-4 w-4 md:mr-0 mr-2" /> <span className="md:hidden">Sil</span>
                                </Button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
